import { useMemo } from 'react';
import { useAdminConfig } from '../useAdmin';
import type { AdminConfigItem } from '../adminApi';
import './AdminConfig.css';

/**
 * Admin configuration view (F14). Read-only dump of the platform's
 * Pydantic settings, grouped by section. Sensitive values (secrets,
 * tokens, DB URLs) are reported as "(set)" / "(unset)" only.
 */
export function AdminConfig(): JSX.Element {
  const { data, isLoading, isError, error } = useAdminConfig();

  const grouped = useMemo(() => {
    const sections = new Map<string, AdminConfigItem[]>();
    for (const item of data?.items ?? []) {
      const list = sections.get(item.section) ?? [];
      list.push(item);
      sections.set(item.section, list);
    }
    return [...sections.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  if (isLoading) {
    return <p className="bl-admin-config__notice">Loading configuration…</p>;
  }
  if (isError) {
    return (
      <p className="bl-admin-config__notice bl-admin-config__notice--error" role="alert">
        Could not load configuration ({error instanceof Error ? error.message : 'unknown error'}).
      </p>
    );
  }
  if (!data) return <p>No data.</p>;

  return (
    <section className="bl-admin-config" aria-label="Configuration">
      <header className="bl-admin-config__header">
        <h2>Configuration</h2>
        <p className="bl-admin-config__sub">
          Read-only view of the runtime settings. Secrets show only whether they&apos;re set, never
          the value.
        </p>
      </header>
      {grouped.map(([section, items]) => (
        <section key={section} className="bl-admin-config__section">
          <h3>{titleCase(section)}</h3>
          <table className="bl-admin-config__table">
            <tbody>
              {items.map((item) => (
                <tr key={item.key}>
                  <th scope="row">{item.key}</th>
                  <td>
                    {item.sensitive ? (
                      <code
                        className={
                          item.value === '(set)' ? 'bl-admin-config__set' : 'bl-admin-config__unset'
                        }
                      >
                        {String(item.value)}
                      </code>
                    ) : (
                      <code>{formatValue(item.value)}</code>
                    )}
                    {item.description && (
                      <span className="bl-admin-config__desc">{item.description}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </section>
  );
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatValue(v: AdminConfigItem['value']): string {
  if (v === null || v === undefined) return '(none)';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}
